// v77.99.70 — "Download read-only PDF" CTA for mediators.
//
// Hidden for everyone except mediators (`isMediator`).
//
// Two modes:
//   • `exportUrl`: open a PDF endpoint directly, appending the bearer
//     token as `?auth=…` (existing PDF endpoints accept query-string
//     auth so the browser can open it in a new tab).
//   • `to`: route to an in-app page (e.g. /app/court-bundle) where
//     the mediator can configure and generate a richer bundle.
//
// Exactly one of `exportUrl` / `to` must be set. Renders a calming
// teal outlined button with a FileDown icon — visually distinct from
// the parent-side exports so it reads as "official read-only export".

import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileDown } from "lucide-react";

export const MediatorPdfDownloadButton = ({
  exportUrl,
  to,
  label = "Download read-only PDF",
  testid = "mediator-pdf-download",
  size = "default",
  className = "",
  disabled = false,
}) => {
  const { isMediator } = useAuth();
  const navigate = useNavigate();
  if (!isMediator) return null;
  const onClick = () => {
    if (to) {
      navigate(to);
      return;
    }
    if (!exportUrl) return;
    // v77.99.102 — Cookie-only download. Same-origin window.open
    // sends the HttpOnly `cpc_token` cookie automatically; the
    // legacy `?auth=<jwt>` query token leaked JWTs into proxy logs
    // and is no longer supported by the backend.
    window.open(exportUrl, "_blank", "noopener");
  };
  return (
    <Button
      onClick={onClick}
      variant="outline"
      size={size}
      disabled={disabled}
      className={`border-[#A6D7D9] text-[#116B6E] hover:bg-[#E0F2F2] rounded-sm ${className}`}
      data-testid={testid}
    >
      <FileDown className="w-4 h-4 mr-1.5" strokeWidth={1.8} />
      {label}
    </Button>
  );
};

export default MediatorPdfDownloadButton;
